
import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";

const Terms = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-4xl">
      <h1 className="text-3xl font-playfair font-bold mb-8 text-heaven-gold">Terms & Conditions</h1>
      
      <div className="text-sm text-gray-600 mb-8">
        <p>Effective Date: July 21, 2024</p>
        <p>Last Updated: April 18, 2025</p>
      </div>

      <div className="prose max-w-none">
        <p className="mb-6">
          Selah Bible Tools is a product of MNP Enterprises LLC. These Terms & Conditions ("Terms") govern your access to and use of the Selah Bible Tools website, mobile application, and all associated services (collectively, the "Service").
        </p>

        <div className="mb-8">
          By using the Service, you agree to these Terms. If you do not agree, please discontinue use of the platform.
        </div>

        <section id="purpose" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">1. Purpose of the Platform</h2>
          <p className="mb-4">
            Selah Bible Tools is designed to equip users with biblically faithful, AI-powered tools for spiritual growth, prayer, discipleship, evangelism, and theological exploration. This platform is intended to support your walk with Christ but is not a replacement for pastoral care, professional counseling, or church accountability.
          </p>
          <p>You agree to use Selah only for lawful purposes, in alignment with these Terms and your local laws.</p>
        </section>

        <section id="stability" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">2. Platform Stability & Service Interruptions</h2>
          <p className="mb-4">
            While we aim to maintain a seamless and stable experience, Selah Bible Tools and MNP Enterprises LLC cannot guarantee the Service will be available at all times or free from error, interruption, or delay.
          </p>
          <p className="mb-4">We are not responsible for:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Outages or downtime caused by updates, bugs, or maintenance</li>
            <li>Errors resulting from third-party services (such as AI providers or cloud infrastructure)</li>
            <li>Data loss due to crashes or interruptions</li>
            <li>Compatibility issues on certain browsers or devices</li>
          </ul>
          <p>Use of Selah is at your own risk.</p>
        </section>

        <section id="ip" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">3. Intellectual Property</h2>
          <p className="mb-4">
            All content, code, branding, visuals, and tool architecture associated with Selah Bible Tools is the intellectual property of MNP Enterprises LLC. This includes:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>The name "Selah Bible Tools"</li>
            <li>Tool designs and system logic</li>
            <li>User interface elements and media</li>
            <li>AI prompts, structure, and formatting</li>
            <li>All original graphics and written content</li>
          </ul>
          <p>Unauthorized use, reproduction, or redistribution of platform elements is strictly prohibited.</p>
        </section>

        <section id="ai-disclaimer" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">4. AI-Generated Content Disclaimer</h2>
          <p className="mb-4">Selah uses artificial intelligence to generate:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Prayers</li>
            <li>Scripture-based responses</li>
            <li>Evangelism simulations</li>
            <li>Interfaith dialogue tools</li>
            <li>Theological guidance</li>
            <li>Word etymology, commentaries, and study plans</li>
          </ul>
          <p>
            While our AI is carefully trained and structured for biblical fidelity, no AI tool is perfect. All content is offered for spiritual support, not doctrinal authority. We recommend comparing all generated output against Scripture and seeking counsel from your local church leadership.
          </p>
        </section>

        {/* Continue with remaining sections */}
        <section id="liability" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">5. Limitation of Liability</h2>
          <p className="mb-4">
            To the fullest extent allowed by law, MNP Enterprises LLC and Selah Bible Tools are not liable for:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Any direct or indirect damages related to the use or inability to use the Service</li>
            <li>Errors, omissions, or incomplete data</li>
            <li>Loss of content or data</li>
            <li>Misuse of platform tools</li>
            <li>Misinterpretation of AI-generated responses</li>
          </ul>
          <p>You agree that you are solely responsible for your use of the platform and any outcomes related to it.</p>
        </section>

        <section id="modifications" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">6. Modifications and Updates</h2>
          <p>
            We may update the Service, improve functionality, or revise these Terms at any time. When we do, we will update the "Last Updated" date at the top of this page. Continued use of Selah after any such changes constitutes your agreement to the new Terms.
          </p>
        </section>

        <section id="account" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">7. Account Creation</h2>
          <p className="mb-4">
            If you create an account with us, you agree to provide accurate and up-to-date information. You are responsible for securing your login credentials and for all activity under your account.
          </p>
          <p>
            We reserve the right to suspend or terminate accounts that violate these Terms or are used in ways that harm others or disrupt the platform.
          </p>
        </section>

        <section id="law" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">8. Governing Law</h2>
          <p>
            These Terms are governed by and interpreted under the laws of the State of Maryland, USA, without regard to its conflict of laws principles.
          </p>
        </section>

        <section id="contact" className="mb-8">
          <h2 className="text-2xl font-playfair font-semibold mb-4 text-[#403E43]">9. Contact Information</h2>
          <p>If you have questions about these Terms, please reach out to:</p>
          <p className="mt-2">
            📧 info@SelahTools.com<br />
            📍 MNP Enterprises LLC<br />
            Baltimore, Maryland, USA
          </p>
        </section>
      </div>

      <Button 
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 rounded-full p-3 bg-heaven-gold hover:bg-heaven-light-gold"
      >
        <ArrowUp className="h-5 w-5" />
      </Button>
    </div>
  );
};

export default Terms;
